import { useTranslation } from 'react-i18next'
import { AlertCircle, CheckCircle2, ChevronDown, ChevronRight, Circle, XCircle } from 'lucide-react'
import { toast } from 'sonner'
import * as DropdownMenuPrimitive from '@radix-ui/react-dropdown-menu'
import { Badge } from '@/ui/components/common/Badge'
import { useSetLeadConclusion } from '@/application/services/lead.service'
import type { Lead } from '@/domains/types'
import { markdownToPlainExcerpt } from '@/ui/utils/markdown'
import { LeadFacts } from './LeadFacts'

interface LeadRowProps {
  lead: Lead
  open: boolean
  onToggle: () => void
  isActiveLead: boolean
  isActiveInvestigation: boolean
  onSetActiveLead: (leadId: string | null) => void
  investigationId: string
  activeFactId?: string | null
  activeLeadId?: string | null
}

/**
 * Single lead row inside an investigation accordion. Shows the lead's
 * title/description, submitter info, an "active lead" toggle, and lazily
 * mounts {@link LeadFacts} when expanded.
 */
export function LeadRow({
  lead,
  open,
  onToggle,
  isActiveLead,
  isActiveInvestigation,
  onSetActiveLead,
  investigationId,
  activeFactId,
  activeLeadId,
}: LeadRowProps) {
  const { t } = useTranslation()
  const plainDescription = markdownToPlainExcerpt(lead.description)
  const label = lead.title || plainDescription || '—'
  const { mutate: setLeadConclusion, isPending: isSettingConclusion } = useSetLeadConclusion()

  return (
    <div className={`group ${open ? 'bg-muted/20' : ''}`}>
      <button
        onClick={onToggle}
        className="w-full flex items-center gap-3 pl-10 pr-5 py-3 hover:bg-muted/50 transition-colors text-left"
      >
        {open ? (
          <ChevronDown className="w-3.5 h-3.5 text-muted-foreground shrink-0" />
        ) : (
          <ChevronRight className="w-3.5 h-3.5 text-muted-foreground shrink-0" />
        )}

        <div className="w-7 h-7 rounded bg-primary/10 flex items-center justify-center shrink-0 overflow-hidden">
          {lead.imageUrl ? (
            <img src={lead.imageUrl} alt="" className="w-full h-full object-cover" />
          ) : (
            <span className="text-xs font-bold text-primary">{label.charAt(0).toUpperCase()}</span>
          )}
        </div>

        <div className="flex-1 min-w-0">
          <span className="text-sm font-medium text-foreground truncate block">
            {lead.title || plainDescription}
          </span>
          {lead.title && lead.description && (
            <span className="text-xs text-muted-foreground truncate block">{plainDescription}</span>
          )}
        </div>

        <span className="text-xs text-muted-foreground shrink-0 hidden sm:block whitespace-nowrap">
          {lead.submitterUsername}
          {' · '}
          {new Date(lead.createdAt).toLocaleDateString('fr-FR')}
        </span>

        {!isActiveLead && isActiveInvestigation && (
          <button
            onClick={(event) => {
              event.stopPropagation()
              onSetActiveLead(lead.id)
            }}
            title={t('admin.live_set_active_lead')}
            className="text-xs px-2 py-1 rounded border border-border transition-colors shrink-0 hover:bg-primary/10 hover:text-primary"
          >
            {t('admin.live_set_active_lead')}
          </button>
        )}
        {isActiveLead && (
          <Badge
            variant="outline"
            className="text-xs shrink-0 border-primary/30 text-primary hidden sm:flex"
          >
            {t('admin.live_active_lead_badge')}
          </Badge>
        )}
        <DropdownMenuPrimitive.Root>
          <DropdownMenuPrimitive.Trigger asChild>
            <button
              type="button"
              onClick={(event) => {
                event.preventDefault()
                event.stopPropagation()
              }}
              disabled={isSettingConclusion}
              title={t('admin.lead_conclusion_label')}
              className="p-1 rounded bg-background/80 border border-border transition-colors disabled:opacity-50 hover:bg-muted/50 shrink-0"
            >
              {lead.conclusion === 'true' ? (
                <CheckCircle2 className="w-3.5 h-3.5 text-success" />
              ) : lead.conclusion === 'false' ? (
                <XCircle className="w-3.5 h-3.5 text-destructive" />
              ) : lead.conclusion === 'fallacious' ? (
                <AlertCircle className="w-3.5 h-3.5 text-warning" />
              ) : lead.conclusion === 'completed' ? (
                <CheckCircle2 className="w-3.5 h-3.5 text-info" />
              ) : (
                <Circle className="w-3.5 h-3.5 text-muted-foreground" />
              )}
            </button>
          </DropdownMenuPrimitive.Trigger>
          <DropdownMenuPrimitive.Portal>
            <DropdownMenuPrimitive.Content
              align="end"
              sideOffset={4}
              onClick={(event) => event.stopPropagation()}
              className="z-50 w-44 bg-background border border-border rounded-lg shadow-lg overflow-hidden"
            >
              {([null, 'true', 'false', 'fallacious', 'completed'] as const).map((value) => (
                <DropdownMenuPrimitive.Item
                  key={value ?? 'none'}
                  onSelect={() => {
                    setLeadConclusion(
                      { id: lead.id, conclusion: value },
                      {
                        onSuccess: () => toast.success(t('admin.lead_conclusion_success')),
                        onError: () =>
                          toast.error(t('admin.lead_conclusion_failed', { error: '' })),
                      },
                    )
                  }}
                  className={`w-full px-3 py-2 text-xs transition-colors cursor-pointer outline-none select-none data-[highlighted]:bg-accent ${
                    lead.conclusion === value || (value === null && !lead.conclusion)
                      ? 'font-semibold text-primary'
                      : ''
                  }`}
                >
                  {value === null
                    ? t('admin.inv_conclusion_none')
                    : value === 'true'
                      ? t('admin.inv_conclusion_true')
                      : value === 'false'
                        ? t('admin.inv_conclusion_false')
                        : value === 'fallacious'
                          ? t('admin.inv_conclusion_fallacious')
                          : t('admin.inv_conclusion_completed')}
                </DropdownMenuPrimitive.Item>
              ))}
            </DropdownMenuPrimitive.Content>
          </DropdownMenuPrimitive.Portal>
        </DropdownMenuPrimitive.Root>
      </button>

      {/* Facts — fetched lazily when the row opens */}
      {open && (
        <div className="pl-20 pr-5 pb-4 pt-1">
          <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-2">
            {t('lead_detail.facts')}
          </p>
          <LeadFacts
            leadId={lead.id}
            investigationId={investigationId}
            activeFactId={activeFactId}
            activeLeadId={activeLeadId}
            isActiveInvestigation={isActiveInvestigation}
          />
        </div>
      )}
    </div>
  )
}
